/**
 * Audit Log copy, merged under `companySettings.audit` for each locale.
 *
 * Action labels are keyed by the raw action string (which contains dots), so they live in a plain
 * lookup instead of the i18next key tree. An action with no entry falls back to a readable form of
 * its own name, never to a blank.
 */
const auditActionLabels: Record<string, { en: string; fa: string }> = {
  'ai.proposal.approved': { en: 'AI proposal approved', fa: 'پیشنهاد هوش مصنوعی تأیید شد' },
  'ai.proposal.build_failed': {
    en: 'AI proposal build failed',
    fa: 'ساخت پیشنهاد هوش مصنوعی ناموفق بود',
  },
  'ai.proposal.built': { en: 'AI proposal built', fa: 'پیشنهاد هوش مصنوعی ساخته شد' },
  'ai.proposal.rejected': { en: 'AI proposal rejected', fa: 'پیشنهاد هوش مصنوعی رد شد' },
  'ai.proposal.submitted': { en: 'AI proposal submitted', fa: 'پیشنهاد هوش مصنوعی ارسال شد' },
  'asset.upload.completed': { en: 'Asset upload completed', fa: 'بارگذاری دارایی کامل شد' },
  'company.ai_provider.removed': {
    en: 'AI provider removed',
    fa: 'ارائه‌دهندهٔ هوش مصنوعی حذف شد',
  },
  'company.settings.updated': { en: 'Company settings updated', fa: 'تنظیمات شرکت به‌روز شد' },
  'external_identity.linked': { en: 'External identity linked', fa: 'هویت بیرونی متصل شد' },
  'external_identity.unlinked': {
    en: 'External identity unlinked',
    fa: 'اتصال هویت بیرونی قطع شد',
  },
  'member.access.changed': { en: 'Member access changed', fa: 'دسترسی عضو تغییر کرد' },
  'member.invitation.accepted': { en: 'Invitation accepted', fa: 'دعوت‌نامه پذیرفته شد' },
  'member.invitation.resent': { en: 'Invitation resent', fa: 'دعوت‌نامه دوباره ارسال شد' },
  'member.invitation.revoked': { en: 'Invitation revoked', fa: 'دعوت‌نامه لغو شد' },
  'member.invited': { en: 'Member invited', fa: 'عضو دعوت شد' },
  'member.removed': { en: 'Member removed', fa: 'عضو حذف شد' },
  'narration.generation.completed': { en: 'Narration audio generated', fa: 'صوت روایت تولید شد' },
  'narration.generation.failed': {
    en: 'Narration generation failed',
    fa: 'تولید روایت ناموفق بود',
  },
  'narration.generation.requested': {
    en: 'Narration generation requested',
    fa: 'تولید روایت درخواست شد',
  },
  'presentation.access.changed': {
    en: 'Presentation access changed',
    fa: 'دسترسی ارائه تغییر کرد',
  },
  'presentation.access.removed': { en: 'Presentation access removed', fa: 'دسترسی ارائه حذف شد' },
  'presentation.appearance.updated': {
    en: 'Presentation appearance updated',
    fa: 'ظاهر ارائه به‌روز شد',
  },
  'presentation.created': { en: 'Presentation created', fa: 'ارائه ساخته شد' },
  'presentation.duplicated': { en: 'Presentation duplicated', fa: 'ارائه کپی شد' },
  'presentation.copy.proposed': {
    en: 'Copy to another Project proposed',
    fa: 'کپی به پروژه دیگر پیشنهاد شد',
  },
  'presentation.copy.approved': {
    en: 'Copy to another Project approved',
    fa: 'کپی به پروژه دیگر تأیید شد',
  },
  'presentation.copy.rejected': {
    en: 'Copy to another Project rejected',
    fa: 'کپی به پروژه دیگر رد شد',
  },
  'presentation.copy.executed': {
    en: 'Presentation copied to another Project',
    fa: 'ارائه به پروژه دیگر کپی شد',
  },
  'presentation.publish.requested': { en: 'Publish requested', fa: 'انتشار درخواست شد' },
  'presentation.published': { en: 'Presentation published', fa: 'ارائه منتشر شد' },
  'presentation.share_link.created': { en: 'Share link created', fa: 'پیوند اشتراک ساخته شد' },
  'presentation.share_link.revoked': { en: 'Share link revoked', fa: 'پیوند اشتراک لغو شد' },
  'presentation.share_link.updated': { en: 'Share link updated', fa: 'پیوند اشتراک به‌روز شد' },
  'project.access.changed': { en: 'Project access changed', fa: 'دسترسی پروژه تغییر کرد' },
  'project.access.removed': { en: 'Project access removed', fa: 'دسترسی پروژه حذف شد' },
  'project.classification_changed': {
    en: 'Project classification changed',
    fa: 'طبقه‌بندی پروژه تغییر کرد',
  },
  'project.created': { en: 'Project created', fa: 'پروژه ساخته شد' },
  'project.deleted': { en: 'Project deleted', fa: 'پروژه حذف شد' },
  'project.design_kit.updated': { en: 'Design kit updated', fa: 'کیت طراحی به‌روز شد' },
  'project.updated': { en: 'Project updated', fa: 'پروژه به‌روز شد' },
  'template.draft.approved': { en: 'Template draft approved', fa: 'پیش‌نویس قالب تأیید شد' },
  'template.draft.published': { en: 'Template draft published', fa: 'پیش‌نویس قالب منتشر شد' },
  'template.draft.rejected': { en: 'Template draft rejected', fa: 'پیش‌نویس قالب رد شد' },
  'template.draft.revised': { en: 'Template draft revised', fa: 'پیش‌نویس قالب بازبینی شد' },
  'template.fork.created': { en: 'Template forked', fa: 'قالب شاخه‌گیری شد' },
  'theme.version.created': { en: 'Theme version created', fa: 'نسخهٔ تم ساخته شد' },
};

/** Event-type groups for the action filter. The value is the prefix the API matches. */
export const AUDIT_ACTION_GROUPS = [
  'narration.',
  'presentation.',
  'project.',
  'member.',
  'company.',
  'asset.',
  'ai.',
  'template.',
  'theme.',
  'external_identity.',
] as const;

export const AUDIT_RESOURCE_TYPES = [
  'Narration',
  'Presentation',
  'Slide',
  'Project',
  'Company',
  'CompanyMembership',
  'Invitation',
  'ShareLink',
  'Release',
  'Asset',
  'AiPresentationProposal',
  'TemplateVersion',
  'ThemeVersion',
] as const;

export function auditActionLabel(action: string, language: string): string {
  const entry = auditActionLabels[action];
  if (entry) return language.startsWith('fa') ? entry.fa : entry.en;
  const words = action.replace(/[._]+/gu, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : action;
}

const stagesEn = {
  AUDIO_VALIDATION: {
    hint: 'The provider returned audio that failed validation (format, size or header). Nothing was stored.',
    label: 'Invalid audio',
  },
  DATA_POLICY: {
    hint: 'The Project classification is not allowed for this provider under the configured data policy.',
    label: 'Data policy',
  },
  PROVIDER_CONFIG: {
    hint: 'The Company provider configuration, stored key, voice or model could not be used.',
    label: 'Provider configuration',
  },
  PROVIDER_HTTP: {
    hint: 'The provider rejected the request or could not be reached. Check the HTTP status.',
    label: 'Provider HTTP error',
  },
  PROVIDER_RESPONSE: {
    hint: 'The provider answered, but not with usable audio.',
    label: 'Provider response',
  },
  PROVIDER_TIMEOUT: {
    hint: 'The provider did not answer within the allowed time.',
    label: 'Provider timeout',
  },
  QUEUE: {
    hint: 'The job could not be queued or was redelivered by the queue.',
    label: 'Queue',
  },
  STORAGE: {
    hint: 'The audio could not be read from or written to object storage.',
    label: 'Storage',
  },
  UNKNOWN: { hint: 'The failure did not match a known category.', label: 'Unknown' },
} as const;

const stagesFa = {
  AUDIO_VALIDATION: {
    hint: 'ارائه‌دهنده صوتی برگرداند که اعتبارسنجی نشد (قالب، اندازه یا سرآیند). چیزی ذخیره نشد.',
    label: 'صوت نامعتبر',
  },
  DATA_POLICY: {
    hint: 'طبقه‌بندی پروژه با سیاست دادهٔ پیکربندی‌شده برای این ارائه‌دهنده مجاز نیست.',
    label: 'سیاست داده',
  },
  PROVIDER_CONFIG: {
    hint: 'پیکربندی ارائه‌دهندهٔ شرکت، کلید ذخیره‌شده، صدا یا مدل قابل استفاده نبود.',
    label: 'پیکربندی ارائه‌دهنده',
  },
  PROVIDER_HTTP: {
    hint: 'ارائه‌دهنده درخواست را رد کرد یا در دسترس نبود. وضعیت HTTP را بررسی کنید.',
    label: 'خطای HTTP ارائه‌دهنده',
  },
  PROVIDER_RESPONSE: {
    hint: 'ارائه‌دهنده پاسخ داد، اما صوت قابل‌استفاده‌ای نبود.',
    label: 'پاسخ ارائه‌دهنده',
  },
  PROVIDER_TIMEOUT: {
    hint: 'ارائه‌دهنده در زمان مجاز پاسخ نداد.',
    label: 'زمان‌بر شدن ارائه‌دهنده',
  },
  QUEUE: { hint: 'کار در صف قرار نگرفت یا صف آن را دوباره تحویل داد.', label: 'صف' },
  STORAGE: {
    hint: 'صوت از فضای ذخیره‌سازی خوانده یا در آن نوشته نشد.',
    label: 'ذخیره‌سازی',
  },
  UNKNOWN: { hint: 'خطا با هیچ دستهٔ شناخته‌شده‌ای مطابقت نداشت.', label: 'نامشخص' },
} as const;

const reasonsEn = {
  CLASSIFICATION_BLOCKED: 'Classification not allowed',
  EMPTY_AUDIO: 'Empty audio',
  HTTP_STATUS: 'HTTP status',
  INTERACTION_STATUS: 'Request did not complete',
  INVALID_BASE64: 'Audio was not valid base64',
  INVALID_JSON: 'Response was not JSON',
  INVALID_SHAPE: 'Unexpected response shape',
  INVALID_VOICE: 'Invalid voice',
  KEY_MISSING: 'No API key',
  KEY_UNREADABLE: 'Stored key cannot be decrypted',
  MIME_TYPE: 'Unexpected audio type',
  NETWORK: 'Connection failed',
  NO_AUDIO: 'No audio returned',
  PAYLOAD_TOO_LARGE: 'Audio too large',
  PCM_FORMAT: 'Unexpected PCM format',
  PCM_SIZE: 'Odd PCM length',
  PERSIST: 'Could not save result',
  PROVIDER_DISABLED: 'Provider disabled',
  REFERENCE_AUDIO_UNSUPPORTED: 'Reference audio unsupported',
  SCRIPT_TOO_LONG: 'Script too long',
  STORAGE_READ: 'Storage read failed',
  STORAGE_WRITE: 'Storage write failed',
  TIMEOUT: 'Timed out',
  UNSUPPORTED_LOCALE: 'Unsupported locale',
  UNSUPPORTED_MODEL: 'Unsupported model',
  VOICE_UNAVAILABLE: 'Voice unavailable',
  WAV_FORMAT: 'Unexpected audio format',
  WAV_HEADER: 'Invalid WAV header',
  WAV_SIZE: 'Inconsistent WAV size',
} as const;

const reasonsFa = {
  CLASSIFICATION_BLOCKED: 'طبقه‌بندی مجاز نیست',
  EMPTY_AUDIO: 'صوت خالی',
  HTTP_STATUS: 'وضعیت HTTP',
  INTERACTION_STATUS: 'درخواست کامل نشد',
  INVALID_BASE64: 'صوت base64 معتبر نبود',
  INVALID_JSON: 'پاسخ JSON نبود',
  INVALID_SHAPE: 'ساختار پاسخ غیرمنتظره',
  INVALID_VOICE: 'صدای نامعتبر',
  KEY_MISSING: 'کلید API وجود ندارد',
  KEY_UNREADABLE: 'کلید ذخیره‌شده رمزگشایی نمی‌شود',
  MIME_TYPE: 'نوع صوت غیرمنتظره',
  NETWORK: 'اتصال برقرار نشد',
  NO_AUDIO: 'صوتی برنگشت',
  PAYLOAD_TOO_LARGE: 'حجم صوت بیش از حد',
  PCM_FORMAT: 'قالب PCM غیرمنتظره',
  PCM_SIZE: 'طول PCM فرد است',
  PERSIST: 'ذخیرهٔ نتیجه ممکن نشد',
  PROVIDER_DISABLED: 'ارائه‌دهنده غیرفعال است',
  REFERENCE_AUDIO_UNSUPPORTED: 'صوت مرجع پشتیبانی نمی‌شود',
  SCRIPT_TOO_LONG: 'متن بیش از حد طولانی است',
  STORAGE_READ: 'خواندن از ذخیره‌سازی ناموفق بود',
  STORAGE_WRITE: 'نوشتن در ذخیره‌سازی ناموفق بود',
  TIMEOUT: 'زمان به پایان رسید',
  UNSUPPORTED_LOCALE: 'زبان پشتیبانی نمی‌شود',
  UNSUPPORTED_MODEL: 'مدل پشتیبانی نمی‌شود',
  VOICE_UNAVAILABLE: 'صدا در دسترس نیست',
  WAV_FORMAT: 'قالب صوتی غیرمنتظره',
  WAV_HEADER: 'سرآیند WAV نامعتبر',
  WAV_SIZE: 'اندازهٔ WAV ناسازگار',
} as const;

export const auditLogCopy = {
  en: {
    allMatching: 'All matching events',
    allTime: 'All time',
    rangeFilter: 'Date range',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    contextUnavailable: 'Context unavailable',
    currentPage: 'On this page',
    eventOverview: 'Event overview',
    noOutcomeTag: 'No outcome recorded',
    openResource: 'Open resource',
    presentation: 'Presentation',
    project: 'Project',
    quickActions: 'Useful actions',
    quickActionsHint: 'Open a verified resource or inspect its other events.',
    relatedActivity: 'View related activity',
    relatedEntities: 'Related entities',
    relatedSubtitle: 'Verified relationships for this event.',
    shareEvents: 'Share link events',
    showingRange: 'Showing {{start}}–{{end}} of {{total}} events',
    slideEvents: 'Slide events',
    summary: 'Activity summary',
    systemEvents: 'System events',
    technicalDetails: 'Technical details',
    totalEvents: 'Total events',
    where: 'Where',
    activityTitle: 'Activity history',
    activitySubtitle: 'Explore changes and inspect the full context of each event.',
    filtersTitle: 'Search and filter audit events',
    readonlyNote: 'Read-only record',
    actionFilter: 'Event type',
    allActions: 'All event types',
    allResources: 'All resources',
    attempts: 'Attempts',
    audioDuration: 'Audio length',
    characters: 'Script length (characters)',
    clearFilters: 'Clear filters',
    close: 'Close',
    copied: 'Copied',
    copy: 'Copy',
    copyFailed: 'Could not copy',
    copyValue: 'Copy {{label}}',
    detailsTitle: 'Event details',
    duration: 'Processing time',
    emptyDescription: 'Events are recorded when people or background jobs change this Company.',
    emptyFilteredDescription:
      'No events match these filters. Try fewer filters or a different search.',
    emptyFilteredTitle: 'No matching events',
    emptyTitle: 'No audit events yet',
    eventId: 'Event ID',
    failureCategory: 'Failure category',
    failureCode: 'Failure code',
    failureReason: 'Reason',
    model: 'Model',
    noDetails: 'No outcome details were recorded for this event.',
    noDetailsHint:
      'Events recorded before outcome tracking do not carry details, and none are inferred.',
    openDetails: 'View details',
    outcome: 'Status',
    outcomes: { COMPLETED: 'Completed', FAILED: 'Failed', REQUESTED: 'Requested' },
    provider: 'Provider',
    providerErrorStatus: 'Provider error status',
    providerStatus: 'Provider HTTP status',
    reasons: reasonsEn,
    resourceFilter: 'Resource type',
    resourceId: 'Resource ID',
    classificationChange: 'Classification',
    lessRestrictiveNote: 'Made less restrictive (Owner/Admin confirmed)',
    retryable: 'Retried automatically',
    search: 'Search events',
    searchPlaceholder: 'Search action, actor, request or resource ID',
    stages: stagesEn,
    systemActor: 'System (background job)',
    timeFull: 'Time',
    yes: 'Yes',
    no: 'No',
    assetVersion: 'Audio asset version',
  },
  fa: {
    allMatching: 'همهٔ نتایج منطبق',
    allTime: 'همهٔ زمان‌ها',
    rangeFilter: 'بازهٔ زمانی',
    last7Days: '۷ روز گذشته',
    last30Days: '۳۰ روز گذشته',
    last90Days: '۹۰ روز گذشته',
    contextUnavailable: 'ارتباط منبع در دسترس نیست',
    currentPage: 'در این صفحه',
    eventOverview: 'نمای کلی رویداد',
    noOutcomeTag: 'نتیجه‌ای ثبت نشده',
    openResource: 'باز کردن منبع',
    presentation: 'ارائه',
    project: 'پروژه',
    quickActions: 'اقدام‌های کاربردی',
    quickActionsHint: 'منبع معتبر را باز کنید یا رویدادهای مرتبط را ببینید.',
    relatedActivity: 'نمایش فعالیت‌های مرتبط',
    relatedEntities: 'منابع مرتبط',
    relatedSubtitle: 'ارتباط‌های تأییدشدهٔ این رویداد.',
    shareEvents: 'رویدادهای لینک اشتراک',
    showingRange: 'نمایش {{start}} تا {{end}} از {{total}} رویداد',
    slideEvents: 'رویدادهای اسلاید',
    summary: 'خلاصهٔ فعالیت‌ها',
    systemEvents: 'رویدادهای سامانه',
    technicalDetails: 'جزئیات فنی',
    totalEvents: 'کل رویدادها',
    where: 'کجا',
    activityTitle: 'تاریخچهٔ فعالیت‌ها',
    activitySubtitle: 'تغییرات را بررسی کنید و جزئیات هر رویداد را ببینید.',
    filtersTitle: 'جستجو و فیلتر رویدادهای ممیزی',
    readonlyNote: 'سوابق فقط‌خواندنی',
    actionFilter: 'نوع رویداد',
    allActions: 'همهٔ انواع رویداد',
    allResources: 'همهٔ منابع',
    attempts: 'تعداد تلاش',
    audioDuration: 'طول صوت',
    characters: 'طول متن (نویسه)',
    clearFilters: 'پاک‌کردن فیلترها',
    close: 'بستن',
    copied: 'کپی شد',
    copy: 'کپی',
    copyFailed: 'کپی ممکن نشد',
    copyValue: 'کپی {{label}}',
    detailsTitle: 'جزئیات رویداد',
    duration: 'زمان پردازش',
    emptyDescription:
      'رویدادها هنگامی ثبت می‌شوند که افراد یا کارهای پس‌زمینه این شرکت را تغییر دهند.',
    emptyFilteredDescription:
      'رویدادی با این فیلترها پیدا نشد. فیلترها را کم کنید یا جستجوی دیگری بزنید.',
    emptyFilteredTitle: 'رویداد منطبقی نیست',
    emptyTitle: 'هنوز رویداد ممیزی‌ای نیست',
    eventId: 'شناسهٔ رویداد',
    failureCategory: 'دستهٔ خطا',
    failureCode: 'کد خطا',
    failureReason: 'علت',
    model: 'مدل',
    noDetails: 'جزئیات نتیجه‌ای برای این رویداد ثبت نشده است.',
    noDetailsHint: 'رویدادهای پیش از ثبت نتیجه جزئیات ندارند و چیزی برای آن‌ها حدس زده نمی‌شود.',
    openDetails: 'مشاهدهٔ جزئیات',
    outcome: 'وضعیت',
    outcomes: { COMPLETED: 'کامل شد', FAILED: 'ناموفق', REQUESTED: 'درخواست شد' },
    provider: 'ارائه‌دهنده',
    providerErrorStatus: 'وضعیت خطای ارائه‌دهنده',
    providerStatus: 'وضعیت HTTP ارائه‌دهنده',
    reasons: reasonsFa,
    resourceFilter: 'نوع منبع',
    resourceId: 'شناسهٔ منبع',
    classificationChange: 'طبقه‌بندی',
    lessRestrictiveNote: 'کم‌محدودیت‌تر شد (با تأیید مالک/مدیر)',
    retryable: 'تلاش خودکار دوباره',
    search: 'جستجوی رویدادها',
    searchPlaceholder: 'جستجوی عملیات، عامل، شناسهٔ درخواست یا منبع',
    stages: stagesFa,
    systemActor: 'سامانه (کار پس‌زمینه)',
    timeFull: 'زمان',
    yes: 'بله',
    no: 'خیر',
    assetVersion: 'نسخهٔ دارایی صوتی',
  },
} as const;
